import { useNavigate, useLocation } from 'react-router-dom'
import {
  Search,
  Settings,
  LayoutGrid,
  LogOut,
  CircleUser,
  Receipt,
  CreditCard,
  Monitor,
  Store,
  Radar,
  MessageSquare,
  TrendingUp,
  Calculator,
  Target,
  Hash,
  Link2,
  ArrowRight,
} from 'lucide-react'
import { Button } from '@shared/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@shared/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu'
import { Popover, PopoverContent, PopoverTrigger } from '@shared/components/ui/popover'
import { useAuthStore } from '@shared/hooks/useAuthStore'
import type { MerchantService } from '@shared/types/merchant'
import { cn } from '@shared/lib/utils'
import { TopBarNotifications } from './TopBarNotifications'
import { TopBarAssistant } from './TopBarAssistant'
import { TopBarHelp } from './TopBarHelp'

interface TopBarActionsProps {
  onSearchOpen?: () => void
}

const PRODUCT_ITEMS: {
  service: MerchantService
  label: string
  icon: typeof Receipt
  href: string
}[] = [
  { service: 'billing', label: 'Billing', icon: Receipt, href: '/settings/billing' },
  { service: 'pos', label: 'POS / In-store', icon: Monitor, href: '/settings/pos' },
  { service: 'ecommerce', label: 'Ecommerce', icon: Store, href: '/ecommerce' },
  { service: 'payment-links', label: 'Payment Links', icon: Link2, href: '/payment-links' },
  { service: 'sonar', label: 'Sonar', icon: Radar, href: '/sonar' },
  { service: 'engagement', label: 'Engagement', icon: MessageSquare, href: '/settings/engagement' },
  { service: 'performance', label: 'Performance', icon: TrendingUp, href: '/payments-channels/performance' },
  { service: 'cost', label: 'Cost Analytics', icon: Calculator, href: '/payments-channels/cost' },
  { service: 'opportunities', label: 'Opportunities', icon: Target, href: '/payments-channels/opportunities' },
  { service: 'bins', label: 'BIN Lookup', icon: Hash, href: '/payments-channels/bins' },
  { service: 'tokens', label: 'Tokens', icon: CreditCard, href: '/payments-channels/tokens' },
]

const iconBtnBase = 'h-8 w-8 transition-colors'
const activeClass = 'bg-accent text-accent-foreground'

export function TopBarActions({ onSearchOpen }: TopBarActionsProps) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const logout = useAuthStore((s) => s.logout)
  const enabledServices = useAuthStore((s) => s.enabledServices) ?? []

  const isSettingsActive = pathname.startsWith('/settings')
  const isPersonalActive = pathname.startsWith('/settings/personal')

  return (
    <div className="flex items-center gap-0.5">
      {/* Search */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={iconBtnBase}
            onClick={onSearchOpen}
          >
            <Search className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Search (⌘K)</TooltipContent>
      </Tooltip>

      {/* Products */}
      <Popover>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className={iconBtnBase}>
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>Products</TooltipContent>
        </Tooltip>
        <PopoverContent align="end" className="w-[280px] p-0">
          <div className="px-4 py-3 border-b border-border">
            <h3 className="text-sm font-semibold">Products</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Manage your enabled products.</p>
          </div>
          <div className="max-h-[360px] overflow-y-auto p-1.5 space-y-0.5">
            {PRODUCT_ITEMS.map((item) => {
              const enabled = enabledServices.includes(item.service)
              return (
                <button
                  key={item.service}
                  onClick={() => navigate(item.href)}
                  className="flex items-center gap-2.5 w-full px-2.5 py-2 text-sm rounded-md transition-colors hover:bg-muted text-left"
                >
                  <item.icon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="flex-1 truncate">{item.label}</span>
                  <span
                    className={cn(
                      'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium border',
                      enabled
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-muted text-muted-foreground border-border'
                    )}
                  >
                    {enabled ? 'On' : 'Off'}
                  </span>
                </button>
              )
            })}
          </div>
          <div className="border-t border-border px-4 py-2.5">
            <button
              onClick={() => navigate('/settings')}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              View all in Settings
              <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Notifications */}
      <TopBarNotifications />

      {/* Assistant */}
      <TopBarAssistant />

      {/* Help */}
      <TopBarHelp />

      {/* Settings */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(iconBtnBase, isSettingsActive && !isPersonalActive && activeClass)}
            onClick={() => navigate('/settings')}
          >
            <Settings className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Settings</TooltipContent>
      </Tooltip>

      {/* User menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(iconBtnBase, isPersonalActive && activeClass)}
          >
            <CircleUser className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={() => navigate('/settings/personal')}>
            <CircleUser className="mr-2 h-4 w-4" />
            Personal settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout}>
            <LogOut className="mr-2 h-4 w-4" />
            Exit
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
